'use client';

import React, { useState } from 'react';
import { Camera, X, ChevronLeft, ChevronRight } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

// Backend integration point: Replace with API call GET /api/gallery/professional
const professionalPhotos = [
{
  id: 'pro-001',
  src: "https://images.unsplash.com/photo-1704281657558-352922cc0463",
  alt: 'Bride and groom exchanging vows inside a stone medieval church with arched windows',
  caption: 'The Ceremony'
},
{
  id: 'pro-002',
  src: "https://images.unsplash.com/photo-1623205112371-7e084a9b2052",
  alt: 'Wedding couple portrait on coastal cliffs with ocean and blue sky behind',
  caption: 'After the Ceremony'
},
{
  id: 'pro-003',
  src: "https://images.unsplash.com/photo-1722372232264-448cc0ff3246",
  alt: 'Wedding guests celebrating outdoors on a sunny terrace with champagne glasses raised',
  caption: 'The Celebration'
},
{
  id: 'pro-004',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_19c5068fc-1772227326796.png",
  alt: 'Wedding reception table decorated with flowers and candles with river views beyond',
  caption: 'The Reception'
},
{
  id: 'pro-005',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce4b5831-1772256921896.png",
  alt: 'Bride and groom first dance in a waterfront venue with warm evening light',
  caption: 'First Dance'
},
{
  id: 'pro-006',
  src: "https://images.unsplash.com/photo-1548785556-791b7fc6e8a5",
  alt: 'Wedding fireworks over the river with silhouettes of guests watching from the waterfront',
  caption: 'The Fireworks'
}];


const isPublished = false; // Toggle to true post-wedding to show photos

export default function ProfessionalGallery() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prev = () => setLightboxIndex((i) => i !== null ? (i - 1 + professionalPhotos.length) % professionalPhotos.length : 0);
  const next = () => setLightboxIndex((i) => i !== null ? (i + 1) % professionalPhotos.length : 0);

  return (
    <section className="pt-12 pb-24 px-8 md:px-16 xl:px-24">
      <div className="max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between mb-14">
          <div>
            <p className="section-label mb-4">Photography</p>
            <h2 className="font-serif text-display-md text-faded-navy">
              Through the Lens
            </h2>
          </div>
          {isPublished &&
          <p className="font-sans text-xs text-stone tracking-[0.1em]">
              {professionalPhotos.length} photographs
            </p>
          }
        </div>

        {!isPublished ? (
        /* Coming soon state */
        <div className="border border-dashed border-border py-28 flex flex-col items-center justify-center text-center">
            <Camera size={36} strokeWidth={1} className="text-warm-sand mb-8" />
            <h3 className="font-serif text-2xl text-faded-navy mb-4">
              Photographs coming soon
            </h3>
            <div className="h-px bg-border w-12 mx-auto mb-6" />
            <p className="font-sans text-sm text-stone max-w-md leading-relaxed">
              Check back here, we will post them as soon as they are ready.
            </p>
            <p className="font-sans text-xs text-stone/50 mt-6 tracking-[0.1em] uppercase">
              Expected: October 2026
            </p>
          </div>) : (

        /* Gallery grid */
        <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-1">
              {professionalPhotos.map((photo, index) =>
            <div
              key={photo.id}
              className="gallery-item relative aspect-square overflow-hidden cursor-pointer group"
              onClick={() => openLightbox(index)}>
              
                  <AppImage
                src={photo.src}
                alt={photo.alt}
                fill
                className="object-cover transition-transform duration-800 group-hover:scale-105"
                sizes="(max-width: 768px) 50vw, 33vw" />
              
                  <div className="gallery-overlay absolute inset-0 bg-deep-navy/40 opacity-0 transition-opacity duration-400 flex items-end p-4">
                    <p className="font-sans text-xs text-off-white tracking-[0.1em]">{photo.caption}</p>
                  </div>
                </div>
            )}
            </div>

            {/* Lightbox */}
            {lightboxIndex !== null &&
          <div
            className="fixed inset-0 z-50 bg-deep-navy/95 flex items-center justify-center"
            onClick={closeLightbox}>
            
                <button
              className="absolute top-6 right-6 text-off-white/60 hover:text-off-white transition-colors duration-300"
              onClick={closeLightbox}
              aria-label="Close lightbox">
              
                  <X size={24} />
                </button>
                <button
              className="absolute left-6 top-1/2 -translate-y-1/2 text-off-white/60 hover:text-off-white transition-colors duration-300"
              onClick={(e) => {e.stopPropagation();prev();}}
              aria-label="Previous photo">
              
                  <ChevronLeft size={32} />
                </button>
                <button
              className="absolute right-6 top-1/2 -translate-y-1/2 text-off-white/60 hover:text-off-white transition-colors duration-300"
              onClick={(e) => {e.stopPropagation();next();}}
              aria-label="Next photo">
              
                  <ChevronRight size={32} />
                </button>
                <div
              className="relative max-w-4xl w-full mx-16 aspect-video"
              onClick={(e) => e.stopPropagation()}>
              
                  <AppImage
                src={professionalPhotos[lightboxIndex].src}
                alt={professionalPhotos[lightboxIndex].alt}
                fill
                className="object-contain"
                sizes="90vw"
                priority />
              
                </div>
                <div className="absolute bottom-8 left-0 right-0 text-center">
                  <p className="font-sans text-xs text-off-white/60">
                    {professionalPhotos[lightboxIndex].caption} · {lightboxIndex + 1} / {professionalPhotos.length}
                  </p>
                </div>
              </div>
          }
          </>)
        }
      </div>
    </section>);

}